import React, { useState, useCallback, useEffect, memo, useMemo } from 'react'
import styled from 'styled-components'
import { StyledCollapseTrigger } from '../formHelper'
import { Collapse, Spinner } from 'react-bootstrap'
import { ArrowBigIcon } from '../../../Icons/ArrowBigIcon'
import { colors } from '../../../../styles/colors'
import { useObserver } from 'mobx-react'
import useStores from '../../../../stores/useStore'
import { t } from 'i18next'
import StorehousesTree from '../../../Storehouses/StorehousesTree'
import { emptyGuid } from '../../../../utils/emptyGuid'
import { StyledValidationMessage } from '../formHelper'

const StyledStorehouseTree = styled.div`
  padding:1rem;
  background: ${colors.gray1};
  margin-bottom:1rem;
  height:400px;
  overflow:auto;
  border: 1px solid ${colors.gray3};
  width:100%;
  @media screen and (min-width: 1080px) {
    max-width: calc(100% - 480px);
    min-width: 600px;
  }
  .checkBox {
      display: flex;
      flex-grow: 1;
      justify-content: flex-end;
      align-items: center;  
   }
`

const StorehouseMultiselect = memo(({
    collapsible,
    accountData,
    setAccountDataByKey,
    formErrors
}) => {
    const { storehouseTreeStore } = useStores()
    const storehouseNodes = useObserver(() => storehouseTreeStore.loadedStorehouses)
    const allStorehousesLoaded = useObserver(() => storehouseTreeStore.allLoaded)
    const [opened, setOpened] = useState(!collapsible)
    const selectedStorehouses = accountData.accessibleStorehouses
    const selectedLocations = accountData.accessibleLocations

    // Effect to load storehouses
    useEffect(() => {
        storehouseTreeStore.resetStorehouses()
        storehouseTreeStore.setMaxUnitType("storehouse")
        storehouseTreeStore.getStorehouses(emptyGuid)
    }, [storehouseTreeStore])

    const handleClickCollapse = useCallback(() => {
        setOpened(prev => !prev)
    }, [setOpened])

    const handleChange = storehouse => e => {
        const childIds = getAllChildIds(storehouse)
        var newSelectedLocations = [...selectedLocations]
        var newSelectedStorehouses = [...selectedStorehouses]
        for (const id of childIds) {
            let foundItem = storehouseTreeStore.loadedStorehouses.find(obj => obj.id === id)
            switch (foundItem.stockUnitType) {
                case "location":
                    if (e.target.checked) {
                        if (!selectedLocations.includes(id)) {
                            newSelectedLocations.push(id)
                        }
                    }
                    else {
                        let foundIndex = newSelectedLocations.indexOf(id);
                        if (foundIndex > -1) {
                            newSelectedLocations.splice(foundIndex, 1)
                        }
                    }
                    break;
                case "storehouse":
                    if (e.target.checked) {
                        if (!selectedStorehouses.includes(id)) {
                            newSelectedStorehouses.push(id)
                        }
                    }
                    else {
                        let foundIndex = newSelectedStorehouses.indexOf(id);
                        if (foundIndex > -1) {
                            newSelectedStorehouses.splice(foundIndex, 1)
                        }
                    }
                    break;
                default:
                    break;
            }
        }
        setAccountDataByKey("accessibleLocations", newSelectedLocations)
        setAccountDataByKey("accessibleStorehouses", newSelectedStorehouses)
    }
    const getAllChildIds = (storehouse) => {
        const ids = [storehouse.id]
        if (storehouse.hasChildren) {
            const children = getChildStorehouses(storehouse)
            for (const child of children) {
                ids.push(...getAllChildIds(child))
            }
        }
        return ids
    }

    const getChildStorehouses = (parentStorehouse) => {
        return storehouseNodes.filter(storehouse => storehouse.parent && storehouse.parent.id === parentStorehouse.id)
    }
    const getParentStorehouse = useCallback((childStorehouse) => {
        return storehouseNodes.find(storehouse => childStorehouse.parent && childStorehouse.parent.id === storehouse.id);
    }, [storehouseNodes])
    const getAllParentIds = useCallback((storehouse, allParentIds) => {
        const ids = [];
        if (storehouse && storehouse.parent) {
            const parent = getParentStorehouse(storehouse);
            if (!allParentIds.includes(parent.id)) {
                ids.push(storehouse.parent.id, ...getAllParentIds(parent, allParentIds));
            }
        }
        return ids;
    }, [getParentStorehouse])



    const getStorehouseById = useCallback((storehouseId) => {
        return storehouseNodes.find(storehouse => storehouse.id === storehouseId)
    }, [storehouseNodes])

    const parentFolders = useMemo(() => {
        let allParentIds = []
        if (allStorehousesLoaded && storehouseNodes.length < 2000) {
            for (let storehouseId of selectedLocations) {
                let storehouse = getStorehouseById(storehouseId)
                let newParents = getAllParentIds(storehouse, allParentIds)
                allParentIds.push(...newParents)
            }
            for (let storehouseId of selectedStorehouses) {
                let storehouse = getStorehouseById(storehouseId)
                let newParents = getAllParentIds(storehouse, allParentIds)
                allParentIds.push(...newParents)
            }
            return allParentIds
        }
        return allParentIds
    }, [allStorehousesLoaded, storehouseNodes.length, selectedStorehouses, selectedLocations, getStorehouseById, getAllParentIds])

    const renderExtraItemContent = (storehouse) => {
        return (
            <div className="checkBox">
                <input checked={selectedStorehouses.includes(storehouse.id) || selectedLocations.includes(storehouse.id)} onChange={handleChange(storehouse)} className="me-4" type="checkbox" />
            </div>
        )
    }

    // Only render the "Lager" header and storehouses if there are storehouses available
    if (storehouseNodes.length === 0) {
        return null 
    }
    
    return (
        <>
            {collapsible ? (
                <StyledCollapseTrigger onClick={handleClickCollapse}>
                    <h2 className="h1"><ArrowBigIcon variant={opened ? 'down' : 'right'} color={colors.textColor} />{t('storehouseTree:storehouse')}
                    </h2>
                </StyledCollapseTrigger>
            ) : (
                <h2 className="h1">{t('storehouseTree:storehouse')}</h2>
            )}
            <Collapse in={opened}>
                <div className="spacing-down">
                    {!allStorehousesLoaded
                        ? <div className="d-flex justify-content-center p-3"><Spinner animation='border' variant='primary' /></div>
                        : <>
                            <StyledStorehouseTree><StorehousesTree
                                renderExtraItemContent={renderExtraItemContent}
                                parentsWithSelectedChildren={parentFolders}
                                withSearch
                            /></StyledStorehouseTree>
                        </>}
                </div>
            </Collapse>
            {!!formErrors['accountaccessibleStorehouses'] &&
                <StyledValidationMessage>{formErrors['accountaccessibleStorehouses'].msg}</StyledValidationMessage>
              }
        </>
    )
})

export default StorehouseMultiselect